import { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { CheckIcon } from "lucide-react";
import { FaWindows, FaApple } from "react-icons/fa";
import useMusioApi from "@/contexts/client/musio-client-context";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { DeviceType } from "@/utils/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLocation } from "wouter";
import { Helmet } from "@dr.pogodin/react-helmet";
import { NotificationRow } from "@/components/ui/notification-row";

export const DownloadMusio = () => {
  const isMobile = useIsMobile();
  const [_, navigate] = useLocation();

  useEffect(() => {
    if (isMobile) navigate("/musio-plus");
  }, [isMobile, navigate]);

  const { musioApi } = useMusioApi();

  const { data: latestRes, isPending: isAppVersionsPending, isError: isLatestError, } = musioApi.useQuery("get", "/appVersions/latest");

  const latest = latestRes?.data;
  const version = latest?.version ?? "";
  const releaseDate = latest?.dateReleased ?? "";

  const { windowsId, macId } = useMemo(() => {
    const getIdFromUrl = (url?: string) => {
      if (!url) return undefined;
      try {
        const id = new URL(url).searchParams.get("id");
        return id ? Number(id) : undefined;
      } catch {
        return undefined;
      }
    };

    const windowsLink = latest?.devices?.find(d => d.deviceType === DeviceType.Windows)?.downloadUrl;
    const macLink = latest?.devices?.find(d => d.deviceType === DeviceType.MacOs)?.downloadUrl;

    return {
      windowsId: getIdFromUrl(windowsLink),
      macId: getIdFromUrl(macLink),
    };
  }, [latest]);

  const {
    data: windowsDownloadRes,
    isPending: isWinPending,
    isError: isWinError,
  } = musioApi.useQuery("get", "/appVersions/download", {
    params: { query: { id: windowsId! } },
    enabled: !!windowsId,
  });

  const {
    data: macDownloadRes,
    isPending: isMacPending,
    isError: isMacError,
  } = musioApi.useQuery("get", "/appVersions/download", {
    params: { query: { id: macId! } },
    enabled: !!macId,
  });

  const windowsDownloadURL = windowsDownloadRes?.url ?? "";
  const macDownloadURL = macDownloadRes?.url ?? "";
  const loading = isAppVersionsPending || isWinPending || isMacPending;
  const isError = isLatestError || isWinError || isMacError;

  return (
    <>
      <Helmet>
        <title>Download Musio | Free AU, VST3, AAX Plugin</title>
        <meta name="description" content="Download the Musio Plugin for your DAW | AU, VST3, AAX compatible" />
        <meta name={`keywords" content="download, musio, Musio, latest-release, macOS, MacOS, Windows, windows, ${version}`} />
      </Helmet>
      <div className="px-7 py-2 w-full">
        {isError ?
          <div className="py-7">
            <NotificationRow intent="error">There was an error getting the latest Musio download data</NotificationRow>
          </div>
          :
          <>
            <div className="py-7">
              <h2 className="text-xl font-bold text-white">Download Musio</h2>
              <p className="text-sm text-musio-text-light">
                Get the latest plugin installer
              </p>
            </div>
            <div className="w-full grid grid-cols-2 gap-x-2 gap-y-2">
              <div
                className="col-span-2 w-full flex flex-row justify-between items-center px-6 py-4 bg-musio-bg"
              >
                <h3 className="font-semibold text-lg text-musio-light-lilac">
                  Musio v{version}
                </h3>
                <div className="flex flex-row items-baseline space-x-2 text-musio-dark-lilac">
                  <p className="text-sm">released</p>
                  <p className="text-lg">{releaseDate}</p>
                </div>
              </div>

              <div
                className="relative flex flex-col items-center justify-center p-6 bg-musio-bg"
              >
                <FaWindows className="absolute top-4 right-4 text-3xl" style={{ color: "#26A2FB" }} />
                <h3 className="font-semibold text-lg mb-2 text-center">Windows</h3>
                <div className="w-full mb-4">
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      Windows 10 or higher (64-bit)
                    </p>
                  </div>
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      Intel i5 / AMD Ryzen or better
                    </p>
                  </div>
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      8GB RAM (16GB recommended)
                    </p>
                  </div>
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      2G free disk space
                    </p>
                  </div>
                </div>
                {loading ? <LoadingIndicator /> :
                  <a
                    href={windowsDownloadURL} download
                    className="w-full"
                  >
                    <Button
                      className="w-full py-3 bg-musio-green text-musio-mid-gray"
                      type="button"
                    >
                      Download For Windows
                    </Button>
                  </a>
                }
              </div>

              <div
                className="relative flex flex-col items-center justify-center p-6 bg-musio-bg"
              >
                <FaApple className="absolute top-4 right-4 text-3xl" style={{ color: "#B6B3F5" }} />
                <h3 className="font-semibold text-lg mb-2 text-center">Apple</h3>
                <div className="w-full mb-4">
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      macOS 10.14 or higher
                    </p>
                  </div>
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      Intel / Apple Silicon processor
                    </p>
                  </div>
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      8GB RAM (16GB recommended)
                    </p>
                  </div>
                  <div className="flex flex-row items-center mb-1">
                    <span className="w-6 flex justify-center">
                      <CheckIcon style={{ color: "rgba(38, 162, 251, 1)" }} />
                    </span>
                    <p className="font-normal text-sm ml-2 text-musio-dark-lilac">
                      2GB free disk space
                    </p>
                  </div>
                </div>
                {loading ? <LoadingIndicator /> :
                  <a
                    href={macDownloadURL} download
                    className="w-full"
                  >
                    <Button
                      className="w-full py-3 bg-musio-green text-musio-mid-gray"
                      type="button"
                    >
                      Download For macOS
                    </Button>
                  </a>
                }
              </div>
            </div>
          </>
        }
      </div>
    </>
  );
};
